import { useEffect, useMemo, useRef, useState } from 'react'
import { FromLink } from '@/components/FromLink'
import { searchAll } from '@/lib/search'
import { useI18n } from '@/i18n'
import { neonStrings } from '@/i18n/neon'
import { artChip, toolClassLabel } from '@/lib/tools'

type Props = { open: boolean; onClose: () => void }

function highlight(text: string, q: string) {
  if (!q) return text
  const idx = text.toLowerCase().indexOf(q.toLowerCase())
  if (idx < 0) return text
  return (
    <>
      {text.slice(0, idx)}
      <mark>{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  )
}

export function SearchDialog({ open, onClose }: Props) {
  const { t: strings, lang } = useI18n()
  const n = neonStrings(lang)
  const [q, setQ] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const results = useMemo(() => searchAll(q, lang), [q, lang])

  useEffect(() => {
    if (open) {
      setQ('')
      setTimeout(() => inputRef.current?.focus(), 20)
    }
  }, [open])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    if (open) window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div style={{ padding: 12 }}>
          <input
            ref={inputRef}
            className="search-input"
            placeholder={strings.actions.searchPlaceholder}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="dialog-results">
          {q && results.length === 0 && <div style={{ padding: 14, color: 'var(--muted)' }}>{strings.empty}</div>}
          {results.map((hit) => {
            if (hit.type === 'tool') {
              return (
                <FromLink key={'tool-' + hit.item.slug} to={hit.href} onClick={onClose}>
                  <span className="hit-kind hit-kind-tool">{n.hitTool}</span>
                  <div style={{ fontWeight: 600 }}>{highlight(hit.item.name, q)}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                    Tools-DB · {artChip(hit.item.kind)} · {toolClassLabel(hit.item.toolClass)} · {hit.item.slug}
                  </div>
                </FromLink>
              )
            }
            const isChain = Array.isArray((hit.item as { steps?: unknown }).steps) || hit.href.includes('/attack-chains/')
            return (
              <FromLink key={'tech-' + hit.item.slug} to={hit.href} onClick={onClose}>
                <span className={`hit-kind ${isChain ? 'hit-kind-chain' : 'hit-kind-technique'}`}>{isChain ? n.hitChain : n.hitTechnique}</span>
                <div style={{ fontWeight: 600 }}>{highlight(hit.item.title, q)}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                  {hit.item.category} · {hit.item.slug}
                </div>
              </FromLink>
            )
          })}
        </div>
        <div className="dialog-footer">{n.searchFooter} <span>{results.length} {n.results}</span></div>
      </div>
    </div>
  )
}
